import React, { useMemo, useState } from 'react';

import { faPlus } from '@fortawesome/pro-regular-svg-icons';

import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Dialog } from '@buildoutinc/blueprint-react/ui/Dialog';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { deleteRequest, post } from 'helpers/api';

import NewRoleDialog from './new_role_dialog';
import RoleEditor from './role_editor';

import type { RoleManagerProps, RoleSummary } from './types';

const holderLabel = (count: number): string => (
  count === 1 ? '1 user' : `${count} users`
);

export default function RoleManager({
  roles: initialRoles, groups, dependencyRules, canManage, paths,
}: RoleManagerProps) {
  const [roles, setRoles] = useState<RoleSummary[]>(initialRoles);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<RoleSummary | null>(null);

  const editing = useMemo(() => roles.find(role => role.id === editingId) ?? null, [roles, editingId]);

  function reportError(error: unknown, fallback: string) {
    const messages = Array.isArray(error) ? error : [];

    showSnackbar({ text: messages.length > 0 ? messages.join(' ') : fallback, variant: 'danger' });
  }

  // Created, then opened in the editor — the duplicate is only half of duplicate-and-edit, and landing back
  // on the list would leave the new role holding someone else's permission set with nothing prompting a
  // second look at it.
  async function handleCreate(name: string, sourceRole: RoleSummary) {
    setSaving(true);

    try {
      const response = await post(paths.roles, { role: { name, sourceRoleId: sourceRole.id } });
      const created: RoleSummary = response.role;

      setRoles(current => [...current, created]);
      setCreating(false);
      setEditingId(created.id);
      showSnackbar({ text: `${created.name} was created. Adjust its permissions below.`, variant: 'success' });
    } catch (error) {
      reportError(error, 'This role could not be created.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(role: RoleSummary) {
    setSaving(true);

    try {
      await deleteRequest(`${paths.roles}/${role.id}`);
      setRoles(current => current.filter(candidate => candidate.id !== role.id));
      setPendingDelete(null);
      showSnackbar({ text: `${role.name} was deleted.`, variant: 'success' });
    } catch (error) {
      reportError(error, 'This role could not be deleted.');
    } finally {
      setSaving(false);
    }
  }

  function handleSaved(saved: RoleSummary) {
    setRoles(current => current.map(role => (role.id === saved.id ? saved : role)));
    setEditingId(null);
  }

  if (editing) {
    return (
      <RoleEditor
        dependencyRules={dependencyRules}
        groups={groups}
        role={editing}
        rolesPath={paths.roles}
        onCancel={() => setEditingId(null)}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h6 className="mb-1">Roles</h6>
          <p className="mb-0 text-muted fs-small">
            A role is a set of permissions. Changing one changes what everyone holding it can do. Roles
            marked <strong>Buildout role</strong> are provided to every company and cannot be changed —
            copy one to make a version you can edit.
          </p>
        </div>
        {canManage && (
          <Button data-testid="new-role" icon={faPlus} variant="primary" onClick={() => setCreating(true)}>
            New Role
          </Button>
        )}
      </div>

      <Table className="align-middle">
        <Table.Header>
          <Table.Row>
            <Table.Head>Name</Table.Head>
            <Table.Head>Permissions</Table.Head>
            <Table.Head>Assigned</Table.Head>
            <Table.Head />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {roles.map(role => (
            <Table.Row aria-label={role.name} key={role.id}>
              <Table.Cell className="text-nowrap">
                <strong>{role.name}</strong>
                {/* Plain badge rather than a tooltip trigger: "why can't I edit this one" is the first
                    question the list raises, and the answer belongs in the caption above where it is read
                    without hovering anything. */}
                {role.system && <Badge className="ms-2" variant="secondary">Buildout role</Badge>}
              </Table.Cell>
              <Table.Cell>{`${role.granted.length} turned on`}</Table.Cell>
              <Table.Cell>{holderLabel(role.assignedUserCount)}</Table.Cell>
              <Table.Cell className="text-end">
                {canManage && !role.system && (
                  <div className="d-flex justify-content-end gap-2 flex-nowrap">
                    <Button
                      data-testid={`edit-role-${role.id}`}
                      size="sm"
                      variant="secondary"
                      onClick={() => setEditingId(role.id)}
                    >
                      Edit
                    </Button>
                    <Button
                      data-testid={`delete-role-${role.id}`}
                      disabled={role.assignedUserCount > 0}
                      disabledTooltip={
                        role.assignedUserCount > 0
                          ? `Remove this role from its ${holderLabel(role.assignedUserCount)} before deleting it.`
                          : undefined
                      }
                      size="sm"
                      variant="destructive"
                      onClick={() => setPendingDelete(role)}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {creating && (
        <NewRoleDialog
          roles={roles}
          saving={saving}
          onClose={() => setCreating(false)}
          onCreate={handleCreate}
        />
      )}

      <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Delete Role</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <p data-testid="delete-message">
              {`${pendingDelete?.name} will be deleted. This cannot be undone.`}
            </p>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Cancel>Cancel</Dialog.Cancel>
            <Button
              data-testid="confirm-delete"
              disabled={saving}
              variant="destructive"
              onClick={() => pendingDelete && handleDelete(pendingDelete)}
            >
              Delete Role
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </div>
  );
}
